import { RULES } from '../core/config.js';
import { createBall, setBallSpeed } from '../entities/ball.js';
import { PADDLE_MODE, setPaddleSize } from '../entities/paddle.js';
import { CAPSULE_COLORS } from '../render/palette.js';

/**
 * Capsule definitions and the effects they apply.
 * Letter codes follow the arcade original, with one addition: R (Reduce),
 * a hazard capsule that shrinks Vaus.
 */

export const POWERUPS = Object.freeze({
  L: { code: 'L', name: 'Laser', hint: 'Vaus mounts twin cannons', hazard: false },
  E: { code: 'E', name: 'Enlarge', hint: 'Vaus grows wider', hazard: false },
  C: { code: 'C', name: 'Catch', hint: 'Ball sticks until you fire', hazard: false },
  S: { code: 'S', name: 'Slow', hint: 'Ball drops to a crawl', hazard: false },
  D: { code: 'D', name: 'Disruption', hint: 'Splits into three balls', hazard: false },
  B: { code: 'B', name: 'Break', hint: 'Opens the warp gate', hazard: false },
  P: { code: 'P', name: 'Player', hint: 'One extra Vaus', hazard: false },
  R: { code: 'R', name: 'Reduce', hint: 'Vaus shrinks. Bad news', hazard: true },
});

export function capsuleColor(code) {
  return CAPSULE_COLORS[code] || '#b9c0cc';
}

/**
 * Pick a capsule type using round-aware weights.
 * @param {number} round 1-based
 * @param {object} session
 */
export function rollCapsule(round, session) {
  const weights = {
    L: 14,
    E: 16,
    C: 12,
    S: 9,
    D: 12,
    B: round >= 2 && !session.isBoss ? 5 : 0,
    P: 6,
    R: round >= 4 ? 8 : 0,
  };

  if (session.isBoss) {
    weights.L = 30;
    weights.D = 18;
    weights.E = 12;
    weights.S = 4;
  }
  // Do not hand out an effect the player already has running.
  if (session.paddle.mode === PADDLE_MODE.LASER) weights.L = Math.round(weights.L * 0.35);
  if (session.paddle.sizeKey === 'wide') weights.E = Math.round(weights.E * 0.4);
  if (session.warpOpen) weights.B = 0;
  if (session.lives >= RULES.maxLives) weights.P = 0;

  const entries = Object.entries(weights).filter(([, w]) => w > 0);
  const total = entries.reduce((sum, [, w]) => sum + w, 0);
  if (total <= 0) return 'E';

  let roll = Math.random() * total;
  for (const [code, weight] of entries) {
    roll -= weight;
    if (roll <= 0) return code;
  }
  return entries[entries.length - 1][0];
}

/**
 * Apply a collected capsule.
 * @param {string} code
 * @param {object} session
 * @param {object} api { sfx, notify, addScore }
 */
export function applyCapsule(code, session, api) {
  const { paddle } = session;

  switch (code) {
    case 'L':
      paddle.mode = PADDLE_MODE.LASER;
      releaseHeldBalls(session);
      break;

    case 'E':
      setPaddleSize(paddle, 'wide');
      break;

    case 'R':
      setPaddleSize(paddle, 'narrow');
      break;

    case 'C':
      paddle.mode = PADDLE_MODE.CATCH;
      break;

    case 'S':
      for (const ball of session.balls) setBallSpeed(ball, ball.speed * 0.7);
      session.rampHits = 0;
      session.roundTime = Math.max(0, session.roundTime - 20);
      break;

    case 'D':
      splitBalls(session);
      break;

    case 'B':
      session.warpOpen = true;
      break;

    case 'P':
      session.lives = Math.min(RULES.maxLives, session.lives + 1);
      break;

    default:
      break;
  }

  const info = POWERUPS[code];
  api.notify?.(info ? `${info.name}: ${info.hint}` : 'Capsule collected');
  if (info?.hazard) api.sfx?.capsuleBad();
  else api.sfx?.capsule();
}

function releaseHeldBalls(session) {
  for (const ball of session.balls) {
    if (ball.stuck) ball.stuck = false;
  }
}

/** Disruption: every ball in play becomes three. */
function splitBalls(session) {
  const room = RULES.maxBalls - session.balls.length;
  if (room <= 0) return;

  const clones = [];
  for (const ball of session.balls) {
    if (clones.length >= room) break;
    for (const spread of [-0.42, 0.42]) {
      if (clones.length >= room) break;
      const angle = Math.atan2(ball.vy, ball.vx) + spread;
      const clone = createBall({ x: ball.x, y: ball.y, speed: ball.speed, angle });
      clones.push(clone);
    }
  }
  session.balls.push(...clones);
}

/** Reset the per-life paddle state without touching round progress. */
export function clearPaddleEffects(paddle) {
  paddle.mode = PADDLE_MODE.NORMAL;
  setPaddleSize(paddle, 'normal');
}
